import CaptureList from '@/components/CaptureList';
import CelebrateChart from '@/components/CelebrateChart';
import CompletedBlockStatic from '@/components/CompletedBlockStatic';
import ConfettiCelebration from '@/components/Confetti';
import ActionListItem from '@/components/DragAndDrop/ActionListItem';
import DynamicSegmentedButton from '@/components/DynamicSegmentedButton';
import Hero from '@/components/Hero';
import { COMPLETE } from '@/constants/action';
import { useAnimatePlanPages } from '@/contexts/AnimatedPlanPages';
import { GET_ACTIONS, GET_COMPLETED_ACTIONS_BLOCKS } from '@/gql/actions';
import { actionsKeys, celebrateReflectionWeekScopeRoot } from '@/gql/actions/keys';
import { CompletedActionsAndBlocks, GetActionsResponse } from '@/gql/actions/types';
import { keys } from '@/gql/global/keys';
import DashboardLayout from '@/layouts/Dashboard';
import { useCategories } from '@/services/categories/hooks';
import { fetchData } from '@/services/graphql';
import { addCategoryToActions } from '@/services/project/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useUpdateWeeklyPlanByPk, useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { HeroCelebrate } from '@/theme/StaticImages';
import { IconGrowth, IconStarFull } from '@/theme/icons';
import theme from '@/theme/index';
import { Action, RecurringWeekContext } from '@/types/actions';
import { getActionsByCategoryId, getCompletedActions, getDailyActions } from '@/utils/action';
import {
  filterActionsSameDay,
  formatDateToString,
  getRecurringInstancesInRangeCompletionSummary,
  startOfTheWeek,
  endOfTheWeek,
} from '@/utils/calendar';
import { buildWeeklyActionsScopeWhere } from '@/utils/actionsQueryWhere';
import { validateRecurrence } from '@/utils/events';
import { makeRecurringSeriesCompletedShadowRow } from '@/utils/recurringCompletedShadow';
import { convertDurationToMinutes } from '@/utils/index';
import { pageTransition } from '@/utils/pageAnimation';
import rem from '@/utils/rem';
import { trackReflectionEntered } from '@/utils/tracking';
import { Box, Editable, EditablePreview, EditableTextarea, Flex, Grid, HStack, Icon, List, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { motion } from 'framer-motion';
import { filter, flattenDeep, groupBy, isEmpty, map, orderBy, reduce, some, uniqBy } from 'lodash';
import { Children, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import { useWindowSize } from 'usehooks-ts';
import { z } from 'zod';

const chartFilters = [
  {
    value: 0,
    label: <Icon as={IconGrowth} boxSize={rem(14)} />,
  },
  {
    value: 1,
    label: <Icon as={IconStarFull} boxSize={rem(14)} />,
  },
];

const ReflectionsFormSchema = z.object({
  reflection: z.string().optional(),
});

type FormData = z.infer<typeof ReflectionsFormSchema>;

type ReflectionChartDurationContext = {
  tabIndex: number;
  isDailyPlanPage: boolean;
  weekStart: Date;
  weekEnd: Date;
  dayStart: Date;
  dayEnd: Date;
};

function reflectionPlannedMinutesForChart(action: Action, ctx: ReflectionChartDurationContext): number {
  const base = convertDurationToMinutes(action.duration);
  if (!(base > 0)) return 0;
  if (ctx.tabIndex === 1 && action.dateOfStarring == null) return 0;
  if (ctx.isDailyPlanPage || !validateRecurrence(action.event?.recurrence)) {
    return ctx.tabIndex === 0 ? base : base;
  }
  const rangeStart = ctx.isDailyPlanPage ? ctx.dayStart : ctx.weekStart;
  const rangeEnd = ctx.isDailyPlanPage ? ctx.dayEnd : ctx.weekEnd;
  const summary = getRecurringInstancesInRangeCompletionSummary(action, rangeStart, rangeEnd);
  if (summary.inRangeCount === 0) return 0;
  return base * summary.inRangeCount;
}

function reflectionCompletedMinutesForChart(action: Action, ctx: ReflectionChartDurationContext): number {
  const base = convertDurationToMinutes(action.duration);
  if (!(base > 0)) return 0;
  const rangeStart = ctx.isDailyPlanPage ? ctx.dayStart : ctx.weekStart;
  const rangeEnd = ctx.isDailyPlanPage ? ctx.dayEnd : ctx.weekEnd;

  if (ctx.isDailyPlanPage || !validateRecurrence(action.event?.recurrence)) {
    if (action.progressStatus !== COMPLETE) return 0;
    if (ctx.tabIndex === 1 && action.dateOfStarring == null) return 0;
    return base;
  }

  const summary = getRecurringInstancesInRangeCompletionSummary(action, rangeStart, rangeEnd);
  if (summary.completedInRangeCount === 0) return 0;
  if (ctx.tabIndex === 1 && action.dateOfStarring == null) return 0;
  return base * summary.completedInRangeCount;
}

function CelebratePage() {
  const { t } = useTranslation('Celebrate');
  const queryClient = useQueryClient();
  const chartRef = useRef<HTMLDivElement>(null);
  const { width: fullWindowWidth } = useWindowSize();
  const toast = useToast();
  const [tabIndex, setTabIndex] = useState(0);
  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);
  const { data: weeklyPlanId, reflection: weeklyPlanReflections } = useWeeklyPlanId(selectedDate);
  const scheduledDate = addDays(startOfDay(selectedDate), 1);
  const startDate = formatDateToString(startOfTheWeek(selectedDate));
  const date = formatDateToString(selectedDate);

  const { pageNavigationEffect } = useAnimatePlanPages();
  const isDailyPlanPage = useIsDailyPlanPage();

  const updateWeeklyPlanReflections = useUpdateWeeklyPlanByPk(selectedDate);

  const { control, handleSubmit, setValue, getValues } = useForm<FormData>({
    defaultValues: {
      reflection: '',
    },
    resolver: zodResolver(ReflectionsFormSchema),
  });

  const previousReflectionValue = getValues('reflection');

  const { data: categoriesList, status: categoriesStatus } = useCategories();

  const { data: reflectionWeekScopeActionList, isLoading: reflectionWeekScopeLoading } = useQuery({
    queryKey: [...celebrateReflectionWeekScopeRoot, formatDateToString(selectedDate)],
    queryFn: async () => {
      const response = await fetchData<GetActionsResponse>(GET_ACTIONS, {
        where: buildWeeklyActionsScopeWhere(selectedDate),
      });
      return addCategoryToActions(response.action, categoriesList?.category ?? []);
    },
    enabled: !isDailyPlanPage && categoriesStatus === 'success',
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const { data: completedActionsAndBlock, isLoading: completedActionsAndBlockLoading } = useQuery({
    queryKey: [actionsKeys.actionsAndBlock, selectedDate, weeklyPlanId],

    queryFn: () => {
      const dailyPlanWhere = {
        scheduledDate: {
          _gte: addDays(startOfDay(selectedDate), -1),
          _lte: addDays(endOfDay(selectedDate), 1),
        },
      };

      const weeklyPlanWhere = {
        weeklyPlanId: {
          _eq: weeklyPlanId,
        },
      };

      return fetchData<CompletedActionsAndBlocks>(GET_COMPLETED_ACTIONS_BLOCKS, {
        whereActions: {
          category: { id: { _isNull: false } },
          ...(isDailyPlanPage ? dailyPlanWhere : weeklyPlanWhere),
        },
        whereBlock: {
          actions: {
            progressStatus: { _eq: COMPLETE },
            ...(isDailyPlanPage
              ? {
                  scheduledDate: {
                    _eq: scheduledDate,
                  },
                }
              : {
                  ...weeklyPlanWhere,
                }),
          },
        },
      });
    },

    enabled: !!weeklyPlanId,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const allActions = useMemo(
    () =>
      isDailyPlanPage && completedActionsAndBlock?.action
        ? filterActionsSameDay(completedActionsAndBlock?.action, selectedDate)
        : completedActionsAndBlock?.action,
    [completedActionsAndBlock?.action, selectedDate, isDailyPlanPage],
  );

  const reflectionActionUnion = useMemo(() => {
    const primary = completedActionsAndBlock?.action ?? [];
    if (isDailyPlanPage || !reflectionWeekScopeActionList?.length) {
      return primary;
    }
    const byId = new Map<string, Action>();
    for (const a of reflectionWeekScopeActionList) {
      byId.set(a.id, a);
    }
    for (const a of primary) {
      byId.set(a.id, a);
    }
    return Array.from(byId.values());
  }, [completedActionsAndBlock?.action, reflectionWeekScopeActionList, isDailyPlanPage]);

  const completedBlocks = useMemo(
    () => completedActionsAndBlock?.block?.filter((block) => block.isCompleted),
    [completedActionsAndBlock?.block],
  );

  const completedActions = useMemo(
    () => filter(allActions, (el) => el.progressStatus === COMPLETE /* && el.blockId === null*/),
    [allActions],
  );

  const completedActionsInBlocks = useMemo(() => {
    const source = isDailyPlanPage ? allActions : reflectionActionUnion;
    return filter(source, (el) => el.progressStatus === COMPLETE && !el.block?.isCompleted);
  }, [allActions, reflectionActionUnion, isDailyPlanPage]);

  const recurringReflectionShadowRows = useMemo(() => {
    const pool = isDailyPlanPage ? allActions : reflectionActionUnion;
    if (!pool?.length) {
      return [];
    }
    const rangeStart = isDailyPlanPage ? startOfDay(selectedDate) : startOfTheWeek(selectedDate);
    const rangeEnd = isDailyPlanPage ? endOfDay(selectedDate) : endOfTheWeek(selectedDate);

    return pool
      .filter((el) => {
        if (el.block?.isCompleted) return false;
        if (el.progressStatus === COMPLETE) return false;
        if (!validateRecurrence(el.event?.recurrence)) return false;
        const summary = getRecurringInstancesInRangeCompletionSummary(el, rangeStart, rangeEnd);
        return summary.inRangeCount > 0 && summary.hasCompleteInRange;
      })
      .map(makeRecurringSeriesCompletedShadowRow);
  }, [allActions, reflectionActionUnion, isDailyPlanPage, selectedDate]);

  const reflectionCompletedActionsFlat = useMemo(() => {
    const byId = new Map<string, Action>();
    for (const a of completedActionsInBlocks) {
      byId.set(a.id, a);
    }
    for (const a of recurringReflectionShadowRows) {
      const realId = a.recurringShadowOfId;
      if (realId && !byId.has(realId)) {
        byId.set(a.id, a);
      }
    }
    return Array.from(byId.values());
  }, [completedActionsInBlocks, recurringReflectionShadowRows]);

  const chartActionsForWorkByCategory = useMemo(() => {
    if (isDailyPlanPage) {
      return allActions ?? [];
    }
    return reflectionActionUnion?.length ? reflectionActionUnion : allActions ?? [];
  }, [isDailyPlanPage, allActions, reflectionActionUnion]);

  const reflectionChartRange = useMemo(
    () => ({
      weekStart: startOfTheWeek(selectedDate),
      weekEnd: endOfTheWeek(selectedDate),
      dayStart: startOfDay(selectedDate),
      dayEnd: endOfDay(selectedDate),
    }),
    [selectedDate],
  );

  const celebrateRecurringContext = useMemo((): RecurringWeekContext => {
    if (isDailyPlanPage) {
      return {
        weekStart: startOfDay(selectedDate),
        weekEnd: endOfDay(selectedDate),
        granularity: 'day',
      };
    }
    return {
      weekStart: startOfTheWeek(selectedDate),
      weekEnd: endOfTheWeek(selectedDate),
      granularity: 'week',
    };
  }, [isDailyPlanPage, selectedDate]);

  const groupedData = useMemo(
    () => groupBy(chartActionsForWorkByCategory, 'category.name') as { [key: string]: Action[] },
    [chartActionsForWorkByCategory],
  );

  const categoriesInfo = useMemo(() => {
    const catsInfo = completedActions.map((action) => {
      const category = action.category;
      const allActionsPerCat = filter(allActions, (el) => el.category.id === category.id).length;
      const completed = completedActions.filter((el) => el.category.id === category.id).length;

      return {
        ...category,
        allActionsPerCat,
        completed,
      };
    });

    return uniqBy(catsInfo, 'name');
  }, [allActions, completedActions]);

  const maxCategoryDuration = useMemo(() => {
    const ctx: ReflectionChartDurationContext = {
      tabIndex,
      isDailyPlanPage,
      ...reflectionChartRange,
    };
    const sumDuration = map(groupedData, (group) => {
      const filteredGroup = filter(group, (el) => {
        if (tabIndex === 0) {
          return true;
        }
        if (tabIndex === 1) {
          return el.dateOfStarring !== null;
        }
        return false;
      });

      return reduce(filteredGroup, (acc, obj) => acc + reflectionPlannedMinutesForChart(obj, ctx), 0);
    });

    if (!isEmpty(sumDuration)) {
      return Math.max(...sumDuration);
    }

    return 0;
  }, [groupedData, tabIndex, isDailyPlanPage, reflectionChartRange]);

  const groupedActionsByCategory = useMemo(() => {
    const ctx: ReflectionChartDurationContext = {
      tabIndex,
      isDailyPlanPage,
      ...reflectionChartRange,
    };

    const mappedData = map(groupedData, (group) => {
      const completed = filter(group, (el) => {
        if (tabIndex === 0) {
          return true;
        }

        if (tabIndex === 1) {
          return el.dateOfStarring !== null;
        }

        return false;
      });

      const [token, tone] = (group[0]?.category?.color ?? 'gray.300').split('.');

      const categoryAllActionsDuration = group?.reduce((acc, obj) => acc + reflectionPlannedMinutesForChart(obj, ctx), 0);

      const categoryAllCompletedActionsDuration = group?.reduce(
        (acc, obj) => acc + reflectionCompletedMinutesForChart(obj, ctx),
        0,
      );

      const backgroundColor = theme.colors?.[token]?.[tone] ?? group[0]?.category?.color;

      const valueFromDuration =
        categoryAllActionsDuration > 0
          ? categoryAllCompletedActionsDuration / categoryAllActionsDuration
          : group?.length
            ? completed.length / group.length
            : 0;

      return {
        value: valueFromDuration,
        backgroundColor,
        allActions: group?.length,
        categoryAllActionsDuration,
        categoryAllCompletedActionsDuration,
        category: group[0]?.category,
      };
    });

    const groupedActions = [
      ...(filter(
        mappedData,
        (el) => el.categoryAllActionsDuration > 0 || (el.allActions >= 1 && el.value > 0),
      ) ?? []),
    ];

    return orderBy(groupedActions, 'categoryOrder', 'asc');
  }, [groupedData, tabIndex, isDailyPlanPage, reflectionChartRange]);

  const hasAtLeastOneActionCompleted = useMemo(
    () => some(groupedActionsByCategory, (el) => el?.categoryAllCompletedActionsDuration !== 0),
    [groupedActionsByCategory],
  );

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!weeklyPlanId) {
        return;
      }

      if (previousReflectionValue === data?.reflection) {
        return;
      }

      await updateWeeklyPlanReflections.mutateAsync(
        {
          id: weeklyPlanId,
          reflection: data?.reflection,
        },
        {
          onSuccess: () => {
            toast({
              title: t('toasts.reflectionUpdated'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });

            trackReflectionEntered();
          },
          onError: (error) => {
            toast({
              title: error?.response?.errors?.[0]?.message,
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          },
        },
      );
    },
    [previousReflectionValue, toast, updateWeeklyPlanReflections, weeklyPlanId, t],
  );

  const completedGroupedByCategoryActionsInBlocks = useMemo(() => {
    const groupedItems = groupBy(reflectionCompletedActionsFlat, (item) => item.category.name);
    const sortedGroups = orderBy(Object.values(groupedItems), ([category]) => category, 'asc');
    const flattenGroup = flattenDeep(sortedGroups);

    return flattenGroup;
  }, [reflectionCompletedActionsFlat]);

  const getSortedActionsByCategoryId = useCallback(
    (categoryId: string) => {
      if (!completedGroupedByCategoryActionsInBlocks) {
        return [];
      }

      const categoryActions = getActionsByCategoryId(completedGroupedByCategoryActionsInBlocks, categoryId);

      const response = isDailyPlanPage
        ? getDailyActions(categoryActions, selectedDate)
        : categoryActions;

      return orderBy(
        response.map((action) => ({ ...action })),
        'categoryOrder',
        'asc',
      );
    },
    [completedGroupedByCategoryActionsInBlocks, selectedDate, isDailyPlanPage],
  );

  const categories = useMemo(() => {
    if (!categoriesList?.category) {
      return [];
    }

    const aggregatedCategories = categoriesList.category.map((category) => ({
      ...category,
      completedActions: getCompletedActions(getSortedActionsByCategoryId(category.id), 'modifiedAt'),
    }));

    // Filter categories that has at least 1 action completed
    return aggregatedCategories.filter((c) => c.completedActions.length !== 0);
  }, [categoriesList?.category, getSortedActionsByCategoryId]);

  useEffect(() => {
    if (weeklyPlanId) setValue('reflection', weeklyPlanReflections?.trim());
  }, [setValue, weeklyPlanId, weeklyPlanReflections]);

  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: [actionsKeys.actionsAndBlock, selectedDate], exact: true });
      queryClient.resetQueries({ queryKey: keys.weeklyPlan.all._ctx.from(startDate).queryKey, exact: true });
    };
  }, [date, queryClient, selectedDate, startDate]);

  if (completedActionsAndBlockLoading || (!isDailyPlanPage && reflectionWeekScopeLoading)) {
    return (
      <DashboardLayout pageTitle={t('pageTitle')}>
        <Flex alignItems="center" justifyContent="center" width="full" paddingY={rem(80)}>
          <Spinner size="xl" speed="0.65s" thickness={rem(4)} />
        </Flex>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle={t('pageTitle')} paddingInline={0}>
      <Hero background={HeroCelebrate} marginTop={rem(-15)} animated />

      {(!isEmpty(completedActions) || !isEmpty(completedBlocks)) && !completedActionsAndBlockLoading && (
        <ConfettiCelebration nConfetti={4000} />
      )}

      <motion.div
        className="row"
        initial="initial"
        animate="in"
        exit="out"
        variants={pageNavigationEffect}
        transition={pageTransition}
      >
        <Box padding={rem(16)} paddingBottom="2">
          <Grid
            ref={chartRef}
            gridGap={rem(32)}
            gridAutoRows="1fr"
            gridTemplateColumns={{ base: 'repeat(1, 1fr)', xl: 'repeat(2, 1fr)' }}
          >
            {/* First column */}
            <Flex
              flexDirection="column"
              gridGap={rem(32)}
              width={{ base: fullWindowWidth - 32, xl: (fullWindowWidth - 80) / 2 }}
              height="full"
            >
              {/*Actions*/}
              <Flex flexDirection="column">
                <Text as="h5" textStyle="h5SatBlack" marginBottom={rem(20)}>
                  {isDailyPlanPage
                    ? t('sectionTitles.actionsCompleted.thisDay')
                    : t('sectionTitles.actionsCompleted.thisWeek')}
                </Text>

                {!isEmpty(completedGroupedByCategoryActionsInBlocks) && !completedActionsAndBlockLoading ? (
                  <>
                    {Children.toArray(
                      categories.map((category) => (
                        <Box marginBottom={rem(16)}>
                          <CaptureList
                            key={category.id}
                            className={`${category.id}-completed-actions`}
                            category={category}
                            totalDurationOfIncompleteActions=""
                            totalDurationOfPreferredIncompleteActions=""
                            showDurationTimers={false}
                          >
                            <List width="full">
                              {category.completedActions.map((action, index) => (
                                <ActionListItem
                                  key={action.id}
                                  item={action}
                                  index={index}
                                  onDeleteAction={() => null}
                                  onClickCreateBlock={() => null}
                                  readOnly
                                  progressStatusButtonDisabled
                                  recurringWeekContext={celebrateRecurringContext}
                                />
                              ))}
                            </List>
                          </CaptureList>
                        </Box>
                      )),
                    )}
                  </>
                ) : (
                  <Flex alignItems="center" justifyContent="center" width="full" height="full">
                    <Text color="gray.400" fontSize={rem(14)}>
                      {isDailyPlanPage
                        ? t('emptyStates.noActionsCompleted.day')
                        : t('emptyStates.noActionsCompleted.week')}
                    </Text>
                  </Flex>
                )}
              </Flex>

              {/* Blocks */}
              <Flex flexDirection="column">
                <Text as="h5" textStyle="h5SatBlack" marginBottom={rem(20)}>
                  {isDailyPlanPage
                    ? t('sectionTitles.blocksCompleted.thisDay')
                    : t('sectionTitles.blocksCompleted.thisWeek')}
                </Text>

                {!isEmpty(completedBlocks) && !completedActionsAndBlockLoading ? (
                  <VStack overflow="auto" width="full">
                    {Children.toArray(completedBlocks?.map((block) => <CompletedBlockStatic {...block} />))}
                  </VStack>
                ) : (
                  <Flex alignItems="center" justifyContent="center" width="full" height="full">
                    <Text color="gray.400" fontSize={rem(14)}>
                      {isDailyPlanPage
                        ? t('emptyStates.noBlocksCompleted.day')
                        : t('emptyStates.noBlocksCompleted.week')}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>

            {/* Second column */}
            <Flex
              flexDirection="column"
              gridGap={rem(32)}
              width={{ base: fullWindowWidth - 32, xl: (fullWindowWidth - 80) / 2 }}
              height="full"
            >
              {/*Reflections*/}
              <VStack alignItems="start" gap={rem(20)} width="full">
                <Text as="h5" textStyle="h5SatBlack">
                  {isDailyPlanPage ? t('sectionTitles.reflections.day') : t('sectionTitles.reflections.week')}
                </Text>

                <Controller
                  name="reflection"
                  control={control}
                  render={({ field: { ref, value, onChange } }) => (
                    <Editable
                      ref={ref}
                      textStyle="small"
                      overflow="hidden"
                      width="full"
                      borderRadius={4}
                      defaultValue={value}
                      onChange={onChange}
                      onSubmit={() => handleSubmit(onSubmit)()}
                      placeholder={
                        isDailyPlanPage ? t('placeholders.reflection.today') : t('placeholders.reflection.thisWeek')
                      }
                      selectAllOnFocus={false}
                      submitOnBlur
                      value={value}
                    >
                      <EditablePreview
                        width="full"
                        height="full"
                        padding={rem(12)}
                        color={value ? 'text-primary' : 'gray.400'}
                        fontStyle={value ? 'normal' : 'italic'}
                        border={`${rem(1)} solid`}
                        borderColor="gray.900"
                      />
                      <EditableTextarea
                        as={TextareaAutosize}
                        height="full"
                        padding={rem(12)}
                        border={`${rem(1)} solid`}
                        borderColor="gray.900"
                        _focusVisible={{
                          border: `${rem(1)} solid`,
                          borderColor: 'cyan.400',
                          borderRadius: 4,
                        }}
                        resize="none"
                        onFocus={(e) =>
                          e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
                        }
                      />
                    </Editable>
                  )}
                />
              </VStack>

              <Flex flexDirection="column" width="full">
                <HStack justifyContent="space-between" width="full">
                  <Text as="h5" textStyle="h5SatBlack">
                    {tabIndex === 0
                      ? isDailyPlanPage
                        ? t('sectionTitles.workByCategory.total.daily')
                        : t('sectionTitles.workByCategory.total.weekly')
                      : isDailyPlanPage
                        ? t('sectionTitles.workByCategory.starred.daily')
                        : t('sectionTitles.workByCategory.starred.weekly')}
                  </Text>

                  <HStack gap={rem(5)}>
                    {!isEmpty(groupedActionsByCategory) && (
                      <HStack marginRight={3}>
                        <Box
                          width={rem(10)}
                          height={rem(10)}
                          borderRadius={99}
                          backgroundColor="background-secondary"
                        />
                        <Text textStyle="small">{t('chartLabels.planned')}</Text>
                      </HStack>
                    )}

                    {!isEmpty(categoriesInfo) &&
                      !completedActionsAndBlockLoading &&
                      !isEmpty(groupedActionsByCategory) &&
                      hasAtLeastOneActionCompleted && (
                        <HStack marginRight={1}>
                          {Children.toArray(
                            groupedActionsByCategory?.map((el, index) => (
                              <Box
                                zIndex={index > 0 ? 1 : 'auto'}
                                display={el?.categoryAllCompletedActionsDuration === 0 ? 'none' : 'block'}
                                width={rem(10)}
                                height={rem(10)}
                                marginLeft={index > 0 ? '-14px !important' : '0'}
                                borderRadius={99}
                                backgroundColor={el?.category?.color}
                              />
                            )),
                          )}

                          <Text textStyle="small">{t('chartLabels.spent')}</Text>
                        </HStack>
                      )}

                    <DynamicSegmentedButton
                      options={chartFilters}
                      onChange={setTabIndex}
                      startIndex={0}
                      layoutId="Celebrate"
                    />
                  </HStack>
                </HStack>

                {!completedActionsAndBlockLoading && (
                  <Flex
                    alignSelf="center"
                    width={{
                      base: 'full',
                      xl: `calc(${fullWindowWidth / 2}px - 1.88rem)`,
                    }}
                    height="full"
                    paddingTop={rem(32)}
                  >
                    <CelebrateChart
                      data={groupedActionsByCategory}
                      width={fullWindowWidth}
                      maxCategoryDuration={maxCategoryDuration}
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Grid>
        </Box>
      </motion.div>
    </DashboardLayout>
  );
}
export default CelebratePage;